import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Typography,
  Box,
  Avatar,
  Button,
  Stack,
  styled,
  Alert,
  Snackbar,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosInstance from "../../axios/axios";
import { useEncryptService } from "../../shared/hooks";
import { toast } from "react-toastify";

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  onInviteSuccess?: () => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    width: "500px",
    maxWidth: "90vw",
  },
}));

const StyledDialogTitle = styled(DialogTitle)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "20px 24px",
});

const EmailItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "8px 12px",
  borderRadius: "8px",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
}));

const EmailListContainer = styled(Box)({
  maxHeight: "200px",
  marginBottom: "16px",
  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "3px",
    "&:hover": {
      background: "#555",
    },
  },
});

// Utility to generate a color from a string (email)
const avatarColors = [
  "#F44336",
  "#E91E63",
  "#9C27B0",
  "#3F51B5",
  "#2196F3",
  "#009688",
  "#4CAF50",
  "#FF9800",
  "#795548",
  "#607D8B",
  "#00BCD4",
  "#8BC34A",
  "#FFC107",
  "#FF5722",
  "#673AB7",
];
function getColorFromEmail(email: string) {
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % avatarColors.length;
  return avatarColors[index];
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  open,
  onClose,
  onInviteSuccess,
}) => {
  const [emailList, setEmailList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [readOnlyRoleId, setReadOnlyRoleId] = useState<string | null>(null);
  const { doEncrypt } = useEncryptService();

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axiosInstance.post("authentication/org-roles");
        if (response.data.status === 200) {
          const readOnlyRole = response.data.data.find(
            (role: any) => role.name === "Read-Only"
          );
          if (readOnlyRole) {
            setReadOnlyRoleId(readOnlyRole.id);
          }
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
        setError("Failed to fetch roles. Please try again.");
      }
    };

    if (open) {
      fetchRoles();
    }
  }, [open]);

  const validationSchema = Yup.object({
    emails: Yup.string().test(
      "emails",
      'One or more emails are invalid. Use format like "user@example.com"',
      function (value) {
        if (!value) return true;
        const emails = value.split(",").map((email) => email.trim());
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails.every((email) => emailRegex.test(email));
      }
    ),
  });

  const formik = useFormik({
    initialValues: {
      emails: "",
    },
    validationSchema,
    onSubmit: (values) => {
      if (values.emails) {
        const newEmails = values.emails
          .split(",")
          .map((email) => email.trim())
          .filter((email) => email && !emailList.includes(email));

        if (newEmails.length > 0) {
          setEmailList((prev) => [...prev, ...newEmails]);
        }
        formik.setFieldValue("emails", "");
      }
    },
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      formik.handleSubmit();
    } else if (e.key === ",") {
      e.preventDefault();
      const currentValue = formik.values.emails;
      if (currentValue.trim()) {
        formik.setFieldValue("emails", currentValue.trim() + ", ");
      }
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmailList((prev) => prev.filter((email) => email !== emailToRemove));
  };

  const handleSendInvite = async () => {
    try {
      if (!readOnlyRoleId) {
        setError("Role ID not available. Please try again.");
        return;
      }

      setIsLoading(true);
      setError(null);

      const inviteData = emailList.map((email) => ({
        role_id: readOnlyRoleId,
        email: email,
      }));

      const response = await axiosInstance.post(
        "authentication/invite-users",
        inviteData
      );

      if (response.data.status === 200) {
        const responseData = response.data.data;

        const failedUsers = responseData.filter(
          (item: any) => item.status === "failed"
        );

        const successfulUsers = responseData
          .filter((item: any) => item.status === "success")
          .flatMap((item: any) => item.userlist || []);

        if (failedUsers.length > 0) {
          const failedEmails = failedUsers
            .map((user: any) => user.email)
            .join(", ");

          let failedMsg = "Please enter a valid email address.";

          if (typeof failedUsers[0]?.message === "string") {
            failedMsg = failedUsers[0]?.message;
          }
          toast.error(`${failedEmails}: ${failedMsg}`);
        }

        if (successfulUsers.length > 0) {
          const emails = successfulUsers
            .map((user: any) => user.organization_email)
            .join(", ");
          toast.success(`${response.data.message} ${emails}`);
        }

        setSuccess(true);
        setEmailList([]);
        onInviteSuccess?.();

        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 1000);
      } else {
        toast.error(response.data.message || "Failed to send invites");
      }
    } catch (error: any) {
      console.error("Error inviting users:", error);
      toast.error(
        error.response?.data?.message ||
        "Failed to send invites. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledDialogTitle>
        <Typography
          variant="body2"
          sx={{ fontWeight: "bold", fontSize: "18px" }}
        >
          Invite User
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: "text.secondary",
            "&:hover": { color: "text.primary" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          sx={{ mb: 3, fontSize: "14px", color: "grey" }}
        >
          Enter users email IDs (comma-separated) and press Enter.        </Typography>

        <TextField
          fullWidth
          name="emails"
          label="Users"
          placeholder="Enter email id"
          value={formik.values.emails}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          error={formik.touched.emails && Boolean(formik.errors.emails)}
          helperText={formik.touched.emails && formik.errors.emails}
          sx={{ mb: 2 }}
          disabled={isLoading}
        />
        <EmailListContainer >
          <Stack spacing={1} sx={{ maxHeight: "200px", overflowY: "auto" }}
            className="scroll"
          >
            {emailList.map((email, index) => (
              <EmailItem key={index}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: getColorFromEmail(email),
                    fontSize: "14px",
                  }}
                >
                  {email[0].toUpperCase()}
                </Avatar>
                <Typography
                  sx={{
                    flex: 1,
                    maxWidth: "250px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={email}
                >
                  {email}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveEmail(email)}
                  sx={{ color: "error.main" }}
                  disabled={isLoading}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
                <Divider orientation="vertical" flexItem />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    backgroundColor: "action.hover",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  Read Only
                </Typography>
              </EmailItem>
            ))}
          </Stack>
        </EmailListContainer>
        <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendInvite}
            disabled={emailList.length === 0 || isLoading}
            sx={{
              fontSize: "13px",
              textTransform: "none",
              px: 3,
              py: 1,
            }}
            endIcon={
              isLoading ? <CircularProgress size={15} color="inherit" /> : null
            }
          >
            {isLoading ? "Sending..." : "Send Invite"}
          </Button>
        </Box>
      </DialogContent>
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      {/* <Snackbar
                open={success}
                autoHideDuration={2000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity="success">
                    Invites sent successfully!
                </Alert>
            </Snackbar> */}
    </StyledDialog>
  );
};

export default InviteUserModal;
